import { Link } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import { useAuth } from "../contexts/AuthContext";
import { SERVICES, SERVICE_IMAGES, HERO_IMAGE, SPECIALTY_DELIVERIES } from "../i18n";
import { ArrowRight, Package, BatteryCharging, Sparkles, CloudRainWind, Snowflake, Boxes, ShieldCheck, Zap, Heart, Cog, Flower2, Croissant, Newspaper, Pill, Star, Quote, Home, Leaf, Wrench, Megaphone, Briefcase, Code2, FileText, Clock3, Users } from "lucide-react";
import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { useCountUp } from "../hooks/useCountUp";
import PriceCalculator from "../components/PriceCalculator";
import BeforeAfter from "../components/BeforeAfter";
import TrustBadges from "../components/TrustBadges";
import TeamSection from "../components/TeamSection";
import ServiceAreaMap from "../components/ServiceAreaMap";
import TodayCounter from "../components/TodayCounter";
import PLZChecker from "../components/PLZChecker";
import FAQSection from "../components/FAQSection";
import TrustedByBar from "../components/TrustedByBar";
import B2BLeadForm from "../components/B2BLeadForm";
import CertificationsBar from "../components/CertificationsBar";
import GoogleReviews from "../components/GoogleReviews";
import LanguagesWeSpeak from "../components/LanguagesWeSpeak";
import UmzugCalculator from "../components/UmzugCalculator";
import SubscriptionPlans from "../components/SubscriptionPlans";
import AutoteileStory from "../components/AutoteileStory";
import HamburgDistricts from "../components/HamburgDistricts";
import RealGallery from "../components/RealGallery";
import VCardButton from "../components/VCardButton";
import LiveDriverMap from "../components/LiveDriverMap";
import SEOHead from "../components/SEOHead";
import { LOCAL_BUSINESS_SCHEMA, ORGANIZATION_SCHEMA, WEBSITE_SCHEMA, BUSINESS_PROFILE } from "../data/seo";

const SERVICE_ICONS = {
  lieferung: Package,
  escooter_service: BatteryCharging,
  umzug: Boxes,
  reinigung: Sparkles,
  hausreinigung: Home,
  gartenpflege: Leaf,
  autoteile: Wrench,
  regenrinne_reinigung: CloudRainWind,
  winterdienst: Snowflake,
  zweimann_handling: Users,
  plakatservice: Megaphone
};

const SPECIALTY_ICONS = {
  blumen: Flower2,
  backwaren: Croissant,
  zeitungen: Newspaper,
  medikamente: Pill
};

export default function Landing() {
  const { t, lang } = useLang();

  // Capture ?ref=TAL-XXXXXX from URL and persist for later use in booking
  useEffect(() => {
    const url = new URL(window.location.href);
    const ref = url.searchParams.get("ref");
    if (ref && /^TAL-[A-Z0-9]{4,12}$/i.test(ref)) {
      localStorage.setItem("talkani_ref_code", ref.toUpperCase());
    }
  }, []);

  return (
    <div>
      <SEOHead
        title={null}
        description={t.hero?.subtitle || "Talkani — Logistik, Umzug, Reinigung & Service in Hamburg und ganz Deutschland. Schnell, zuverlässig, persönlich."}
        url={BUSINESS_PROFILE.url}
        type="website"
        lang={lang}
        jsonLd={[LOCAL_BUSINESS_SCHEMA, ORGANIZATION_SCHEMA, WEBSITE_SCHEMA]}
      />
      <Hero />
      {/* Owner + team introduction (About with big group photo) */}
      <AboutIntro />
      {/* Norddeutschland-wide animated coverage map */}
      <LiveDriverMap />
      <CertificationsBar />
      <TodayCounter />
      {/* SERVICES FIRST — per user request "اعرض الخدمات أولاً" */}
      <Services />
      {/* Detailed coverage grid — Hamburg-Stadt · Metropolregion · Norddeutschland */}
      <HamburgDistricts />
      <PriceCalculator />
      <PLZChecker />
      {/* Social proof — real partners */}
      <TrustedByBar />
      {/* B2B block */}
      <B2BBanner />
      <B2BLeadForm />
      {/* 4 individual team-member portraits — moved back to lower position */}
      <TeamSection />
      {/* Trust + final CTA */}
      <Testimonials />
      <FAQSection />
      <CtaBanner />
      <Contact />
    </div>
  );
}

function Testimonials() {
  const { t } = useLang();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/testimonials").then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <section className="py-24 lg:py-32 bg-[#F8F9FA]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-5">
          <div className="max-w-3xl">
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#FF5A00] mb-3">{t.testimonials.eyebrow}</p>
            <h2 className="text-4xl sm:text-5xl font-black tracking-tighter mb-4">{t.testimonials.title}</h2>
            <p className="text-base text-neutral-600 leading-relaxed">{t.testimonials.subtitle}</p>
          </div>
          <button
            onClick={() => {
              if (!user) { toast.info(t.testimonials.form_login_required); window.location.href = "/login"; return; }
              setShowForm(true);
            }}
            className="btn-primary self-start"
            data-testid="write-review-btn"
          >
            <Star size={16} className="fill-current" /> {t.testimonials.write_btn}
          </button>
        </div>

        {items.length === 0 ? (
          <div className="border border-dashed border-neutral-300 bg-white p-12 text-center" data-testid="testimonials-empty">
            <Star size={32} className="text-neutral-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">{t.testimonials.empty_title}</h3>
            <p className="text-neutral-600 max-w-md mx-auto">{t.testimonials.empty_body}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((it) => (
              <div key={it.testimonial_id} className="bg-white border border-neutral-200 p-7 lg:p-8 card-lift flex flex-col" data-testid={`testimonial-${it.testimonial_id}`}>
                <Quote size={28} className="text-[#FF5A00] mb-4" strokeWidth={2.5} />
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={`star-${i}`} size={14} className={i < it.rating ? "fill-[#FF5A00] text-[#FF5A00]" : "text-neutral-300"} />
                  ))}
                </div>
                <p className="text-neutral-800 leading-relaxed text-[15px] flex-1">&ldquo;{it.text}&rdquo;</p>
                <div className="mt-6 pt-5 border-t border-neutral-100">
                  <div className="font-bold text-sm">{it.name}</div>
                  {it.role && <div className="text-xs text-neutral-500 mt-0.5">{it.role}</div>}
                </div>
              </div>
            ))}
          </div>
        )}

        {showForm && <ReviewForm onClose={() => setShowForm(false)} onSubmitted={() => { setShowForm(false); load(); }} />}
      </div>
    </section>
  );
}

function ReviewForm({ onClose, onSubmitted }) {
  const { t } = useLang();
  const { user } = useAuth();
  const [form, setForm] = useState({ name: user?.name || "", role: "", rating: 5, text: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.text.trim().length < 5) return;
    setSubmitting(true);
    try {
      await api.post("/testimonials/submit", form);
      toast.success(t.testimonials.form_success);
      onSubmitted();
    } catch (err) {
      toast.error("Error");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white max-w-lg w-full p-8 max-h-[90vh] overflow-y-auto" data-testid="review-form">
        <h3 className="text-2xl font-black tracking-tighter mb-6">{t.testimonials.form_title}</h3>

        <div className="mb-4">
          <label className="text-xs font-bold tracking-widest uppercase text-neutral-600 block mb-2">{t.testimonials.form_name}</label>
          <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="w-full border border-neutral-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#FF5A00]" data-testid="review-name" />
        </div>

        <div className="mb-4">
          <label className="text-xs font-bold tracking-widest uppercase text-neutral-600 block mb-2">{t.testimonials.form_role}</label>
          <input value={form.role} onChange={e => setForm({...form, role: e.target.value})} className="w-full border border-neutral-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#FF5A00]" data-testid="review-role" />
        </div>

        <div className="mb-4">
          <label className="text-xs font-bold tracking-widest uppercase text-neutral-600 block mb-2">{t.testimonials.form_rating}</label>
          <div className="flex gap-1" data-testid="review-rating">
            {[1, 2, 3, 4, 5].map(n => (
              <button type="button" key={n} onClick={() => setForm({...form, rating: n})} className="p-1" data-testid={`rating-star-${n}`}>
                <Star size={28} className={n <= form.rating ? "fill-[#FF5A00] text-[#FF5A00]" : "text-neutral-300"} />
              </button>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <label className="text-xs font-bold tracking-widest uppercase text-neutral-600 block mb-2">{t.testimonials.form_text}</label>
          <textarea value={form.text} onChange={e => setForm({...form, text: e.target.value})} required rows={5} maxLength={1000} className="w-full border border-neutral-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#FF5A00]" data-testid="review-text" />
          <div className="text-xs text-neutral-400 mt-1 text-end">{form.text.length}/1000</div>
        </div>

        <div className="flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="btn-outline text-neutral-700" data-testid="review-cancel-btn">{t.testimonials.form_cancel}</button>
          <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-50" data-testid="review-submit-btn">
            {submitting ? "..." : t.testimonials.form_submit}
          </button>
        </div>
      </form>
    </div>
  );
}

function SpecialtyCard({ keyName, image, Icon, t, idx }) {
  const [ref, visible] = useScrollReveal();
  const delayClass = `sr-d${(idx % 4) + 1}`;
  return (
    <div
      ref={ref}
      className={`group card-lift bg-[#F8F9FA] border border-neutral-200 overflow-hidden sr sr-scale ${delayClass} ${visible ? "is-visible" : ""}`}
      data-testid={`specialty-card-${keyName}`}
    >
      <div className="aspect-[4/5] overflow-hidden bg-neutral-200 relative">
        <img src={image} alt={t.specialty[keyName].name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute top-4 start-4 w-9 h-9 bg-[#FF5A00] text-white flex items-center justify-center rounded-sm shadow-lg">
          <Icon size={18} />
        </div>
      </div>
      <div className="p-6">
        <h3 className="text-lg font-bold tracking-tight mb-2">{t.specialty[keyName].name}</h3>
        <p className="text-sm text-neutral-600 leading-relaxed">{t.specialty[keyName].desc}</p>
      </div>
    </div>
  );
}

function Specialty() {
  const { t } = useLang();
  const [headRef, headVisible] = useScrollReveal();
  return (
    <section className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div ref={headRef} className={`mb-14 max-w-3xl sr ${headVisible ? "is-visible" : ""}`}>
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#FF5A00] mb-3">{t.specialty.eyebrow}</p>
          <h2 className="text-4xl sm:text-5xl font-black tracking-tighter mb-4">{t.specialty.title}</h2>
          <p className="text-base text-neutral-600 leading-relaxed">{t.specialty.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SPECIALTY_DELIVERIES.filter(s => !s.isCustom).map(({ key, image }, idx) => {
            const Icon = SPECIALTY_ICONS[key];
            return (
              <SpecialtyCard key={key} keyName={key} image={image} Icon={Icon} t={t} idx={idx} />
            );
          })}
        </div>
      </div>
    </section>
  );
}

function StatItem({ value, label, index }) {
  const [ref, visible] = useScrollReveal();
  const display = useCountUp(value, { start: visible, duration: 1800 });
  return (
    <div
      ref={ref}
      className={`text-center sr sr-scale sr-d${index + 1} ${visible ? "is-visible" : ""}`}
      data-testid={`about-stat-${index + 1}`}
    >
      <div className="stat-num text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter">{display}</div>
      <div className="mt-1 text-xs sm:text-sm uppercase tracking-wider text-neutral-500 font-medium">{label}</div>
    </div>
  );
}

function AboutIntro() {
  const { t } = useLang();
  const [headRef, headVisible] = useScrollReveal();
  const [imgRef, imgVisible] = useScrollReveal();
  return (
    <section id="about" className="py-20 lg:py-28 bg-white border-b border-neutral-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div ref={headRef} className={`text-center mb-12 lg:mb-16 sr ${headVisible ? "is-visible" : ""}`}>
          <div className="text-xs sm:text-sm tracking-[0.25em] text-orange-600 font-bold mb-3">{t.about_intro.eyebrow}</div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mb-6">{t.about_intro.title}</h2>
          <p className="text-base sm:text-lg text-neutral-600 leading-relaxed max-w-3xl mx-auto">{t.about_intro.body}</p>
        </div>

        <div
          ref={imgRef}
          className={`relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-neutral-200 bg-neutral-900 sr sr-scale ${imgVisible ? "is-visible" : ""}`}
          data-testid="about-team-image-wrapper"
        >
          <img
            src="/generated/team_owner.png"
            alt="Talkani — Logistik · Reinigung · Service — Unser Team"
            className="w-full h-auto object-cover"
            data-testid="about-team-image"
          />
        </div>

        <div className="mt-10 lg:mt-14 grid grid-cols-3 gap-4 sm:gap-8 max-w-4xl mx-auto">
          {[
            { k: "stat1", v: t.about_intro.stat1_value, l: t.about_intro.stat1_label },
            { k: "stat2", v: t.about_intro.stat2_value, l: t.about_intro.stat2_label },
            { k: "stat3", v: t.about_intro.stat3_value, l: t.about_intro.stat3_label }
          ].map((s, i) => (
            <StatItem key={s.k} value={s.v} label={s.l} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Hero() {
  const { t } = useLang();
  return (
    <section className="relative min-h-[640px] flex items-center grain bg-[#090A0B] text-white overflow-hidden">
      <div className="absolute inset-0">
        <video
          className="absolute inset-0 w-full h-full object-cover opacity-60"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster="/videos/hero_showreel_poster.jpg"
          aria-hidden="true"
          data-testid="hero-video"
        >
          <source src="/videos/hero_showreel.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-t from-[#090A0B] via-[#090A0B]/75 to-[#090A0B]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#090A0B]/70 via-transparent to-transparent" />
        <div className="hero-glow" aria-hidden="true" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24 grid md:grid-cols-12 gap-8 w-full">
        <div className="md:col-span-8 reveal reveal-1">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#FF5A00] mb-6">{t.hero.eyebrow}</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.02] mb-6">
            {t.hero.title}
          </h1>
          <p className="text-lg text-neutral-300 max-w-2xl mb-10 leading-relaxed">{t.hero.subtitle}</p>
          <div className="flex flex-wrap gap-4">
            <Link to="/book" className="btn-primary" data-testid="hero-book-btn">
              {t.hero.cta_primary} <ArrowRight size={18} />
            </Link>
            <a href="#services" className="btn-outline text-white" data-testid="hero-services-btn">
              {t.hero.cta_secondary}
            </a>
          </div>
        </div>
        <div className="md:col-span-4 hidden md:flex flex-col justify-end items-end gap-4 reveal reveal-3">
          <div className="hero-stat border border-neutral-700 bg-neutral-900/60 p-5 backdrop-blur-md w-full max-w-xs">
            <div className="text-4xl font-black text-[#FF5A00]">24/7</div>
            <div className="text-xs text-neutral-400 uppercase tracking-widest mt-1">Service</div>
          </div>
          <div className="hero-stat border border-neutral-700 bg-neutral-900/60 p-5 backdrop-blur-md w-full max-w-xs">
            <div className="text-4xl font-black">DE</div>
            <div className="text-xs text-neutral-400 uppercase tracking-widest mt-1">Bundesweit</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Marquee() {
  const items = ["Lieferung", "E-Scooter Service", "Umzug", "Blumen", "Backwaren", "Zeitungen", "Medikamente", "Reinigung", "Hausreinigung", "Gartenpflege", "Autoteile", "Regenrinne", "Winterdienst", "24/7 Express"];
  return (
    <div className="border-y border-neutral-200 bg-white py-6 overflow-hidden">
      <div className="marquee">
        <div className="marquee-track">
          {[...items, ...items, ...items].map((it, i) => (
            <span key={`marquee-${i}-${it}`} className="text-2xl sm:text-3xl font-black tracking-tighter text-neutral-900 flex items-center gap-12">
              {it} <span className="text-[#FF5A00]">●</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function ServiceCard({ keyName, index, isFeature, Icon, t, rankedSubs, abVariants }) {
  const [ref, visible] = useScrollReveal();
  const span = isFeature ? "md:col-span-8" : "md:col-span-4";
  const delayClass = `sr-d${Math.min(6, (index % 6) + 1)}`;
  return (
    <div
      ref={ref}
      className={`${span} card-lift bg-white border border-neutral-200 overflow-hidden flex ${isFeature ? "flex-col md:flex-row" : "flex-col"} sr sr-scale ${delayClass} ${visible ? "is-visible" : ""}`}
      data-testid={`service-card-${keyName}`}
    >
      <div className={`${isFeature ? "md:w-1/2" : ""} relative aspect-[4/3] overflow-hidden bg-neutral-200`}>
        <img src={SERVICE_IMAGES[keyName]} alt="" className="absolute inset-0 w-full h-full object-cover" />
      </div>
      <div className="p-6 lg:p-8 flex-1 flex flex-col justify-between">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#FF5A00]/10 text-[#FF5A00] flex items-center justify-center rounded-sm">
              <Icon size={20} />
            </div>
            <span className="service-number text-xs font-bold tracking-widest uppercase text-neutral-500">0{index + 1}</span>
          </div>
          <h3 className="text-xl lg:text-2xl font-bold mb-3 tracking-tight">{t.services[keyName].name}</h3>
          <ServiceDesc text={t.services[keyName].desc} serviceKey={keyName} rankedSubs={rankedSubs} abVariants={abVariants} />
        </div>
        <Link to={`/book?service=${keyName}`} className="inline-flex items-center gap-2 text-sm font-bold mt-6 text-[#FF5A00] hover:gap-3 transition-all">
          {t.nav.book} <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
}

// Renders a service description. If the text contains "•" bullet markers, the
// segments are split into an intro paragraph + a visual list with checkmark
// icons so sub-services like "Treppenhausreinigung" and "Fensterreinigung"
// don't get buried in a long sentence.
//
// When a serviceKey is provided, each bullet becomes a clickable Link that
// jumps to /book?service={serviceKey}&sub={encoded label}. The current
// (already-translated) bullet text is passed verbatim through the URL so the
// booking page can prefill the details field without needing a parallel
// translation map.
function ServiceDesc({ text, serviceKey, rankedSubs, abVariants }) {
  if (!text) return null;
  const idx = text.indexOf("•");
  if (idx === -1) {
    return <p className="text-sm text-neutral-600 leading-relaxed">{text}</p>;
  }
  const intro = text.slice(0, idx).trim().replace(/[—–-]\s*$/, "").trim();
  const rest = text.slice(idx);
  const parts = rest.split("•").map((s) => s.trim()).filter(Boolean);
  let outro = "";
  if (parts.length > 0) {
    const last = parts[parts.length - 1];
    const dotIdx = last.search(/\.\s+[A-ZÄÖÜ\u0600-\u06FF]/);
    if (dotIdx !== -1) {
      parts[parts.length - 1] = last.slice(0, dotIdx + 1).trim();
      outro = last.slice(dotIdx + 1).trim();
    }
  }
  // A/B variants — prepend their labels as additional bullets at the TOP.
  // They are rendered identically to native bullets and tracked the same way.
  const abBullets = Array.isArray(abVariants) ? abVariants.map((v) => ({ label: v.label, ab: true })) : [];
  // Re-order parts so high-converting sub-services appear first. We match by
  // clean label (trailing period stripped). Items present in `rankedSubs` are
  // placed first in the given order; unmatched items keep their original
  // relative order at the bottom.
  let topLabel = null;
  if (serviceKey && Array.isArray(rankedSubs) && rankedSubs.length > 0) {
    const cleanOf = (s) => s.replace(/\.$/, "").trim();
    const rankIndex = new Map(rankedSubs.map((lbl, i) => [lbl, i]));
    parts.sort((a, b) => {
      const ra = rankIndex.has(cleanOf(a)) ? rankIndex.get(cleanOf(a)) : 1e6;
      const rb = rankIndex.has(cleanOf(b)) ? rankIndex.get(cleanOf(b)) : 1e6;
      return ra - rb;
    });
    if (parts.length > 0 && rankIndex.has(cleanOf(parts[0]))) {
      topLabel = cleanOf(parts[0]);
    }
  }
  const allBullets = [
    ...abBullets,
    ...parts.map((p) => ({ label: p, ab: false })),
  ];
  return (
    <div className="text-sm text-neutral-600 leading-relaxed space-y-2.5">
      {intro && <p>{intro}</p>}
      <ul className="space-y-1.5">
        {allBullets.map((b, i) => {
          const p = b.label;
          const cleanLabel = p.replace(/\.$/, "");
          const isTop = !b.ab && topLabel && cleanLabel === topLabel;
          const content = (
            <>
              <span className="text-[#FF5A00] font-bold mt-0.5 flex-shrink-0">›</span>
              <span className="flex-1">
                {p}
                {b.ab && (
                  <span
                    className="ms-2 inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-purple-100 text-purple-800 rounded-sm align-middle"
                    title="A/B-Variant"
                  >
                    ✨ Neu
                  </span>
                )}
                {isTop && (
                  <span
                    className="ms-2 inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-orange-100 text-[#E04F00] rounded-sm align-middle"
                    title="Beliebt — meist gebucht"
                    data-testid={`service-${serviceKey}-top-badge`}
                  >
                    🔥 Top
                  </span>
                )}
              </span>
            </>
          );
          if (!serviceKey) {
            return <li key={i} className="flex items-start gap-2">{content}</li>;
          }
          // Sub-services that have their own dedicated SEO landing page —
          // clicking the bullet routes the visitor through the landing first
          // (better content, more trust, stronger conversion) instead of
          // jumping straight to the booking form.
          const DEDICATED_LANDINGS = {
            "Graffiti-Entfernung": "/leistungen/graffiti-entfernung-hamburg",
            "Graffiti removal": "/leistungen/graffiti-entfernung-hamburg",
            "إزالة الكتابات والرسومات": "/leistungen/graffiti-entfernung-hamburg",
          };
          const dedicated = Object.keys(DEDICATED_LANDINGS).find(
            (k) => cleanLabel.startsWith(k),
          );
          const href = dedicated
            ? DEDICATED_LANDINGS[dedicated]
            : `/book?service=${serviceKey}&sub=${encodeURIComponent(cleanLabel)}`;
          const handleClick = () => {
            try {
              api.post("/sub-clicks/event", {
                service: serviceKey,
                sub_label: cleanLabel,
                lang: typeof document !== "undefined" ? document.documentElement.lang || "" : "",
              }).catch(() => {});
            } catch { /* noop */ }
          };
          return (
            <li key={i}>
              <Link
                to={href}
                onClick={handleClick}
                className="flex items-start gap-2 -mx-1.5 px-1.5 py-0.5 rounded-sm hover:bg-orange-50 hover:text-[#E04F00] transition-colors group"
                data-testid={`service-${serviceKey}-sub-${i}`}
              >
                {content}
              </Link>
            </li>
          );
        })}
      </ul>
      {outro && <p className="text-xs text-neutral-500 leading-relaxed italic">{outro}</p>}
    </div>
  );
}

function Services() {
  const { t } = useLang();
  const [headRef, headVisible] = useScrollReveal();
  // Live rankings per service — pulled once per page-load. Empty object until
  // the API responds, so first paint uses the default i18n order; once data
  // arrives the bullets quietly re-order with high-converters first.
  const [rankings, setRankings] = useState({});
  // Active A/B-test variants per service. Picked once per visitor session and
  // stored in localStorage so the visitor doesn't see a different variant on
  // each click (which would invalidate the test). The chosen variant is
  // injected as an additional clickable bullet at the TOP of the service card.
  const [abByService, setAbByService] = useState({});
  useEffect(() => {
    let cancelled = false;
    api.get("/services/ranked-subs?days=60")
      .then((r) => { if (!cancelled) setRankings(r.data?.rankings || {}); })
      .catch(() => { /* silent — fall back to default order */ });
    api.get("/ab/active")
      .then((r) => {
        if (cancelled) return;
        const groups = r.data?.groups || {};
        const picks = {};
        const allShown = [];
        Object.entries(groups).forEach(([svc, list]) => {
          const chosen = [];
          list.forEach((g) => {
            if (!g.variants || g.variants.length === 0) return;
            // Stable per-session pick keyed by group_id
            const lsKey = `talkani_ab_${svc}_${g.group_id}`;
            let chosenId = null;
            try { chosenId = localStorage.getItem(lsKey); } catch { /* private */ }
            let variant = g.variants.find((v) => v.variant_id === chosenId);
            if (!variant) {
              variant = g.variants[Math.floor(Math.random() * g.variants.length)];
              try { localStorage.setItem(lsKey, variant.variant_id); } catch { /* noop */ }
            }
            chosen.push(variant);
            allShown.push(variant.variant_id);
          });
          if (chosen.length) picks[svc] = chosen;
        });
        setAbByService(picks);
        // Fire-and-forget impression bump
        if (allShown.length) {
          api.post("/ab/impression", { variant_ids: allShown }).catch(() => {});
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  return (
    <section id="services" className="py-24 lg:py-32 bg-[#F8F9FA]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div ref={headRef} className={`flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6 sr ${headVisible ? "is-visible" : ""}`}>
          <div>
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#FF5A00] mb-3">{t.services.eyebrow}</p>
            <h2 className="text-4xl sm:text-5xl font-black tracking-tighter max-w-2xl">{t.services.title}</h2>
          </div>
          <Link to="/book" className="btn-primary" data-testid="services-book-btn">
            {t.nav.book} <ArrowRight size={18} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {SERVICES.map((key, i) => {
            const Icon = SERVICE_ICONS[key];
            const isFeature = i === 0 || i === 5;
            return (
              <ServiceCard key={key} keyName={key} index={i} isFeature={isFeature} Icon={Icon} t={t} rankedSubs={rankings[key] || null} abVariants={abByService[key] || null} />
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ValueCard({ keyName, Icon, t, idx }) {
  const [ref, visible] = useScrollReveal();
  return (
    <div
      ref={ref}
      className={`bg-[#090A0B] p-8 lg:p-10 sr sr-d${idx + 1} ${visible ? "is-visible" : ""}`}
      data-testid={`value-${keyName}`}
    >
      <div className="w-12 h-12 bg-[#FF5A00] flex items-center justify-center rounded-sm mb-6">
        <Icon size={22} />
      </div>
      <h3 className="text-2xl font-bold tracking-tight mb-3">{t.values[keyName].title}</h3>
      <p className="text-neutral-400 text-sm leading-relaxed">{t.values[keyName].desc}</p>
    </div>
  );
}

function Values() {
  const { t } = useLang();
  const [headRef, headVisible] = useScrollReveal();
  const items = [
    { key: "reliability", icon: ShieldCheck },
    { key: "quality", icon: Cog },
    { key: "flexibility", icon: Zap },
    { key: "satisfaction", icon: Heart }
  ];
  return (
    <section id="about" className="py-24 lg:py-32 bg-[#090A0B] text-white grain">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div ref={headRef} className={`sr ${headVisible ? "is-visible" : ""}`}>
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#FF5A00] mb-3">{t.values.eyebrow}</p>
          <h2 className="text-4xl sm:text-5xl font-black tracking-tighter max-w-2xl mb-16">{t.values.title}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-neutral-800">
          {items.map(({ key, icon: Icon }, i) => (
            <ValueCard key={key} keyName={key} Icon={Icon} t={t} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function B2BBanner() {
  const { t } = useLang();
  const [ref, visible] = useScrollReveal();
  const b = t.b2b_banner || {};
  const industries = [
    b.ind_werkstatt, b.ind_agentur, b.ind_hausverwaltung,
    b.ind_hotel, b.ind_schule, b.ind_shop,
  ].filter(Boolean);
  return (
    <section className="relative bg-[#0A0B0D] text-white overflow-hidden border-y border-neutral-800" data-testid="b2b-banner">
      <div
        className="absolute inset-0 opacity-20 bg-cover bg-center"
        style={{ backgroundImage: "url(/generated/service_autoteile.png)" }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A0B0D] via-[#0A0B0D]/95 to-[#0A0B0D]/70" />
      <div
        ref={ref}
        className={`relative max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 sr ${visible ? "is-visible" : ""}`}
      >
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 bg-[#FF5A00]/15 text-[#FF5A00] px-3 py-1.5 mb-5 border border-[#FF5A00]/30">
            <Briefcase size={14} />
            <span className="text-[10px] font-black tracking-[0.3em] uppercase">{b.eyebrow}</span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] mb-5">
            {b.title}
          </h2>
          <p className="text-base sm:text-lg text-neutral-300 leading-relaxed max-w-2xl mb-8">
            {b.subtitle}
          </p>

          {industries.length > 0 && (
            <div className="mb-8" data-testid="b2b-industries">
              <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-neutral-400 mb-3">
                {b.industries_label}
              </div>
              <div className="flex flex-wrap gap-2">
                {industries.map((label) => (
                  <span
                    key={label}
                    className="text-xs font-bold uppercase tracking-wider bg-white/5 border border-white/15 px-3 py-2 backdrop-blur-sm"
                  >
                    {label}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-3 items-center">
            <a
              href="#b2b-lead"
              className="bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold uppercase tracking-wide px-7 py-4 transition rounded-sm inline-flex items-center gap-2"
              data-testid="b2b-banner-primary"
            >
              {b.cta_primary} <ArrowRight size={16} />
            </a>
            <Link
              to="/b2b"
              className="text-neutral-300 hover:text-white text-xs font-bold uppercase tracking-widest transition-colors inline-flex items-center gap-1.5 px-2 py-4"
              data-testid="b2b-banner-details"
            >
              {b.cta_details || "Alle Details ansehen"}
            </Link>
            <a
              href="/api/docs"
              target="_blank"
              rel="noreferrer"
              className="text-neutral-400 hover:text-white text-xs font-bold uppercase tracking-widest transition-colors inline-flex items-center gap-1.5 px-2 py-4"
              data-testid="b2b-banner-docs"
            >
              <Code2 size={13} /> {b.cta_secondary}
            </a>
          </div>
        </div>

        <div className="lg:col-span-5 grid grid-cols-2 gap-3 self-center">
          <BStat icon={FileText} value={b.stat1} sub={b.stat1_sub} />
          <BStat icon={Code2} value={b.stat2} sub={b.stat2_sub} />
          <BStat icon={ShieldCheck} value={b.stat3} sub={b.stat3_sub} accent />
          <BStat icon={Clock3} value={b.stat4} sub={b.stat4_sub} />
        </div>
      </div>
    </section>
  );
}

function BStat({ icon: Icon, value, sub, accent = false }) {
  return (
    <div
      className={`p-5 border ${accent ? "border-[#FF5A00] bg-[#FF5A00]/10" : "border-white/15 bg-white/5"} backdrop-blur-sm`}
    >
      <Icon size={18} className={accent ? "text-[#FF5A00] mb-2" : "text-neutral-300 mb-2"} />
      <div className={`text-base sm:text-lg font-black tracking-tight ${accent ? "text-[#FF5A00]" : "text-white"}`}>
        {value}
      </div>
      <div className="text-[10px] text-neutral-400 tracking-widest uppercase font-bold mt-1">{sub}</div>
    </div>
  );
}

function CtaBanner() {
  const { t } = useLang();
  const [ref, visible] = useScrollReveal();
  return (
    <section className="bg-[#FF5A00] py-20 cta-shimmer">
      <div
        ref={ref}
        className={`max-w-7xl mx-auto px-6 lg:px-12 flex flex-col md:flex-row items-center justify-between gap-6 sr ${visible ? "is-visible" : ""}`}
      >
        <div>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tighter text-white">{t.cta_banner.title}</h2>
          <p className="text-white/90 mt-2">{t.cta_banner.subtitle}</p>
        </div>
        <Link to="/book" className="bg-white text-[#FF5A00] font-bold tracking-wide px-8 py-4 hover:bg-neutral-100 hover:-translate-y-0.5 transition-all rounded-sm" data-testid="cta-banner-btn">
          {t.cta_banner.button}
        </Link>
      </div>
    </section>
  );
}

function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success(t.contact.sent);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Error");
    } finally { setSending(false); }
  };

  return (
    <section id="contact" className="py-24 lg:py-32 bg-[#F8F9FA]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#FF5A00] mb-3">{t.contact.eyebrow}</p>
          <h2 className="text-4xl sm:text-5xl font-black tracking-tighter mb-8">{t.contact.title}</h2>
          <div className="space-y-5 text-sm">
            <div>
              <div className="text-xs font-bold tracking-widest uppercase text-neutral-500">{t.contact.email_label}</div>
              <a href="mailto:info@talkani.de" className="font-semibold text-base mt-1 block hover:text-[#FF5A00] transition-colors" data-testid="contact-info-email">info@talkani.de</a>
            </div>
            <div>
              <div className="text-xs font-bold tracking-widest uppercase text-neutral-500">{t.contact.phone_label}</div>
              <a href="tel:+491758802646" className="font-semibold text-base mt-1 block hover:text-[#FF5A00] transition-colors" data-testid="contact-info-phone">+49 175 8802646</a>
            </div>
            <div>
              <div className="text-xs font-bold tracking-widest uppercase text-neutral-500">{t.contact.address_label}</div>
              <div className="font-semibold text-base mt-1">Talkani Logistik &amp; Reinigung GmbH <span className="text-[#FF5A00]">i.&nbsp;Gr.</span><br/>Walddörferstraße 383<br/>22047 Hamburg</div>
            </div>
            <div className="pt-2">
              <VCardButton variant="inline" />
            </div>
          </div>
        </div>
        <form onSubmit={submit} className="md:col-span-7 bg-white border border-neutral-200 p-8 lg:p-10 space-y-4" data-testid="contact-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label={t.contact.name} value={form.name} onChange={v => setForm({...form, name: v})} required testid="contact-name" />
            <Field label={t.contact.email} type="email" value={form.email} onChange={v => setForm({...form, email: v})} required testid="contact-email" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label={t.contact.phone} value={form.phone} onChange={v => setForm({...form, phone: v})} testid="contact-phone" />
            <Field label={t.contact.subject} value={form.subject} onChange={v => setForm({...form, subject: v})} required testid="contact-subject" />
          </div>
          <div>
            <label className="text-xs font-bold tracking-widest uppercase text-neutral-600 block mb-2">{t.contact.message}</label>
            <textarea
              value={form.message}
              onChange={e => setForm({...form, message: e.target.value})}
              required
              rows={5}
              className="w-full border border-neutral-300 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#FF5A00] focus:border-transparent"
              data-testid="contact-message"
            />
          </div>
          <button type="submit" disabled={sending} className="btn-primary disabled:opacity-50" data-testid="contact-submit-btn">
            {sending ? t.common.loading : t.contact.send}
          </button>
        </form>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="text-xs font-bold tracking-widest uppercase text-neutral-600 block mb-2">{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        required={required}
        className="w-full border border-neutral-300 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#FF5A00] focus:border-transparent"
        data-testid={testid}
      />
    </div>
  );
}
